// src/components/appSettings/Config/ConfigList.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiSettings,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiSearch,
  FiRefreshCw,
  FiDollarSign,
  FiPercent,
  FiShield,
  FiPackage,
  FiTruck,
  FiCreditCard,
  FiGlobe,
  FiToggleRight,
  FiAlertCircle,
} from "react-icons/fi";
import {
  fetchAllConfigsThunk,
  deleteConfigThunk,
  updateAppConfigThunk,
} from "../../../store/thunks/appSettingsThunks";
import Button from "../../../components/common/Button";
import toast from "react-hot-toast";

const ConfigList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const { configs, loadingList } = useSelector((state) => state.appSettings);
  const { user } = useSelector((state) => state.auth);

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [editingConfig, setEditingConfig] = useState(null);
  const [showQuickEditModal, setShowQuickEditModal] = useState(false);

  // Load configs on mount
  useEffect(() => {
    loadConfigs();
  }, [dispatch]);

  const loadConfigs = useCallback(() => {
    dispatch(fetchAllConfigsThunk());
  }, [dispatch]);

  // Get unique categories
  const categories = [
    "all",
    ...new Set(configs?.map((config) => config.category) || []),
  ];

  // Filter configs
  const filteredConfigs = configs?.filter((config) => {
    const matchesSearch =
      config.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || config.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Group configs by category
  const groupedConfigs = filteredConfigs?.reduce((acc, config) => {
    const category = config.category || "Uncategorized";
    if (!acc[category]) acc[category] = [];
    acc[category].push(config);
    return acc;
  }, {});

  const handleDelete = async (configId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this configuration? This action cannot be undone."
      )
    ) {
      try {
        await dispatch(deleteConfigThunk(configId)).unwrap();
        toast.success("Configuration deleted successfully");
      } catch (error) {
        toast.error(error.message || "Failed to delete configuration");
      }
    }
  };

  const handleQuickEdit = (config) => {
    setEditingConfig(config);
    setShowQuickEditModal(true);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      PRICING: FiDollarSign,
      RATES: FiPercent,
      PAYMENT: FiCreditCard,
      DELIVERY: FiTruck,
      LIMITS: FiShield,
      GENERAL: FiSettings,
      FEATURES: FiToggleRight,
      BUSINESS: FiPackage,
    };
    return icons[category] || FiSettings;
  };

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            App Configuration
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage application settings and configurations
          </p>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={loadConfigs}
            disabled={loadingList}
          >
            <FiRefreshCw className={loadingList ? "animate-spin" : ""} />
          </Button>
          <Button onClick={() => navigate("/app-settings/config/new")}>
            <FiPlus className="mr-2" />
            Add Configuration
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Total Configs"
          value={configs?.length || 0}
          icon={FiSettings}
          color="blue"
        />
        <StatCard
          label="Categories"
          value={categories.length - 1}
          icon={FiPackage}
          color="purple"
        />
        <StatCard
          label="Active"
          value={configs?.filter((c) => c.isActive !== false).length || 0}
          icon={FiToggleRight}
          color="green"
        />
        <StatCard
          label="Modified Today"
          value={
            configs?.filter((c) => {
              const today = new Date().toDateString();
              return new Date(c.updatedAt).toDateString() === today;
            }).length || 0
          }
          icon={FiAlertCircle}
          color="orange"
        />
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search configurations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category === "all"
                  ? "All Categories"
                  : category.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Configurations List */}
      {loadingList ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : filteredConfigs?.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <FiSettings className="mx-auto text-5xl text-gray-400 mb-4" />
          <p className="text-gray-500">No configurations found</p>
          <Button
            className="mt-4"
            onClick={() => navigate("/app-settings/config/new")}
          >
            Create Your First Configuration
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedConfigs || {}).map(([category, configs]) => {
            const Icon = getCategoryIcon(category);
            return (
              <div key={category} className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b bg-gray-50">
                  <div className="flex items-center gap-2">
                    <Icon className="text-gray-600" />
                    <h2 className="text-lg font-medium text-gray-900">
                      {category.replace(/_/g, " ")}
                    </h2>
                    <span className="text-sm text-gray-500">
                      ({configs.length})
                    </span>
                  </div>
                </div>

                <div className="divide-y">
                  {configs.map((config) => (
                    <ConfigRow
                      key={config._id}
                      config={config}
                      onEdit={() =>
                        navigate(`/app-settings/config/edit/${config._id}`)
                      }
                      onQuickEdit={handleQuickEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Quick Edit Modal */}
      {showQuickEditModal && editingConfig && (
        <QuickEditModal
          config={editingConfig}
          onClose={() => {
            setShowQuickEditModal(false);
            setEditingConfig(null);
          }}
          onSave={() => {
            setShowQuickEditModal(false);
            setEditingConfig(null);
            loadConfigs();
          }}
        />
      )}
    </div>
  );
};

// Config Row Component
const ConfigRow = ({ config, onEdit, onQuickEdit, onDelete }) => {
  const getValueDisplay = () => {
    if (config.dataType === "BOOLEAN") {
      return config.value ? "Enabled" : "Disabled";
    }
    if (config.dataType === "JSON") {
      return "Complex Object";
    }
    if (config.dataType === "ARRAY") {
      return `${config.value?.length || 0} items`;
    }
    return String(config.value || "-");
  };

  const getDataTypeColor = () => {
    const colors = {
      STRING: "blue",
      NUMBER: "green",
      BOOLEAN: "purple",
      JSON: "orange",
      ARRAY: "pink",
    };
    return colors[config.dataType] || "gray";
  };

  return (
    <div className="p-6 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-medium text-gray-900">
              {config.name || config.key}
            </h3>
            <span
              className={`inline-flex px-2 py-0.5 text-xs rounded-full bg-${getDataTypeColor()}-100 text-${getDataTypeColor()}-800`}
            >
              {config.dataType}
            </span>
            {config.isRequired && (
              <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">
                Required
              </span>
            )}
            {config.isSecret && (
              <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">
                Secret
              </span>
            )}
          </div>

          {config.description && (
            <p className="text-sm text-gray-600 mb-2">{config.description}</p>
          )}

          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            <span>Key: {config.key}</span>
            <span>
              Value: {config.isSecret ? "••••••••" : getValueDisplay()}
            </span>
            {config.unit && <span>Unit: {config.unit}</span>}
            {config.validation?.min !== undefined && (
              <span>Min: {config.validation.min}</span>
            )}
            {config.validation?.max !== undefined && (
              <span>Max: {config.validation.max}</span>
            )}
            {config.updatedAt && (
              <span>
                Updated: {new Date(config.updatedAt).toLocaleString()}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 ml-4">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onQuickEdit(config)}
            title="Quick Edit"
          >
            <FiEdit2 size={14} />
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={onEdit}
            title="Full Edit"
          >
            <FiSettings size={14} />
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={() => onDelete(config._id)}
            className="text-red-600 hover:bg-red-50"
            title="Delete"
          >
            <FiTrash2 size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Quick Edit Modal Component
const QuickEditModal = ({ config, onClose, onSave }) => {
  const dispatch = useDispatch();
  const [value, setValue] = useState(config.value);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let parsedValue = value;

      // Parse value based on data type
      if (config.dataType === "NUMBER") {
        parsedValue = parseFloat(value);
      } else if (config.dataType === "BOOLEAN") {
        parsedValue = value === "true" || value === true;
      } else if (config.dataType === "JSON") {
        try {
          parsedValue = JSON.parse(value);
        } catch (e) {
          toast.error("Invalid JSON format");
          setSubmitting(false);
          return;
        }
      }

      await dispatch(
        updateAppConfigThunk({
          id: config._id,
          configData: { value: parsedValue },
        })
      ).unwrap();

      toast.success("Configuration updated successfully");
      onSave();
    } catch (error) {
      toast.error(error.message || "Failed to update configuration");
    } finally {
      setSubmitting(false);
    }
  };

  const renderInput = () => {
    switch (config.dataType) {
      case "BOOLEAN":
        return (
          <select
            value={value}
            onChange={(e) => setValue(e.target.value === "true")}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </select>
        );

      case "NUMBER":
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            min={config.validation?.min}
            max={config.validation?.max}
            step={config.validation?.step || "any"}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        );

      case "JSON":
        return (
          <textarea
            value={
              typeof value === "object" ? JSON.stringify(value, null, 2) : value
            }
            onChange={(e) => setValue(e.target.value)}
            rows="8"
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
            placeholder='{"key": "value"}'
          />
        );

      default:
        return (
          <input
            type={config.isSecret ? "password" : "text"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">
            Quick Edit: {config.name || config.key}
          </h2>
          {config.description && (
            <p className="text-sm text-gray-600 mt-1">{config.description}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Value {config.unit && `(${config.unit})`}
            </label>
            {renderInput()}
            {config.validation && (
              <p className="text-xs text-gray-500 mt-1">
                {config.validation.min !== undefined &&
                  `Min: ${config.validation.min} `}
                {config.validation.max !== undefined &&
                  `Max: ${config.validation.max} `}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting} disabled={submitting}>
              {submitting ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-lg shadow p-4">
    <div className="flex items-center justify-between mb-2">
      <Icon className={`text-2xl text-${color}-600`} />
    </div>
    <p className="text-sm text-gray-600">{label}</p>
    <p className={`text-2xl font-bold text-${color}-700`}>{value}</p>
  </div>
);

export default ConfigList;
